import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import { websocketService } from '../services/websocket';

interface CollaborativeNotepadProps {
  className?: string;
}

const SAVE_DEBOUNCE_DELAY = 2000; // 2 seconds

const CollaborativeNotepad: React.FC<CollaborativeNotepadProps> = ({ className }) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId] = useState(() => `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isConnected, setIsConnected] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Generate browser fingerprint for tracking
  const fingerprint = useRef<string>('');

  useEffect(() => {
    // Generate a simple browser fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Browser fingerprint', 2, 2);
      fingerprint.current = canvas.toDataURL().slice(-50);
    }
    fingerprint.current += navigator.userAgent.slice(-20) + screen.width + screen.height;
  }, []);

  // Load initial document content
  const loadDocument = async () => {
    try {
      const response = await apiService.getDocument();
      const loadedContent = response.content || '';
      
      setContent(loadedContent);
      setHasUnsavedChanges(false);
      setIsLoading(false);
      console.log('📄 Document loaded:', { length: loadedContent.length });
    } catch (error) {
      console.error('❌ Failed to load document:', error);
      setContent('');
      setIsLoading(false);
    }
  };

  // Save document to server
  const saveDocument = async (newContent: string) => {
    try {
      setHasUnsavedChanges(false);
      
      console.log('💾 Saving document...', { length: newContent.length, sessionId });
      
      await apiService.updateDocument({
        content: newContent,
        operation: 'replace',
        contentBefore: content, // Use current state, not ref
        positionStart: 0,
        positionEnd: newContent.length,
        sessionId,
        fingerprint: fingerprint.current
      });
      
      console.log('✅ Document saved successfully');
      
      // Broadcast to other users - EXACTLY like chat
      if (isConnected && websocketService.isConnected()) {
        console.log('📤 Broadcasting document update to other users');
        websocketService.sendDocumentUpdate({
          content: newContent,
          operation: 'replace',
          positionStart: 0,
          positionEnd: newContent.length,
          sessionId
        });
      }
      
    } catch (error) {
      console.error('❌ Failed to save document:', error);
      setHasUnsavedChanges(true);
    }
  };

  // Handle text changes - EXACTLY like chat's newMessage handling
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    console.log('✏️ User typed:', { length: newContent.length });
    
    setContent(newContent);
    setHasUnsavedChanges(true);
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce save
    saveTimeoutRef.current = setTimeout(() => {
      console.log('⏰ Save timeout fired, saving document');
      saveDocument(newContent);
    }, SAVE_DEBOUNCE_DELAY);
  };

  // Handle real-time updates - EXACTLY like chat's message handling
  const handleDocumentUpdate = (data: {
    content: string;
    sessionId: string;
    operation: string;
    timestamp: string;
  }) => {
    console.log('📥 Received document update:', { 
      from: data.sessionId, 
      current: sessionId,
      shouldUpdate: data.sessionId !== sessionId 
    });
    
         // Only update if from different session - EXACTLY like chat
     if (data.sessionId !== sessionId) {
       console.log('🔄 Applying remote update from another user');
       
       // Clear any pending saves - remote content is newer
       if (saveTimeoutRef.current) {
         console.log('🗑️ Clearing pending save timeout');
         clearTimeout(saveTimeoutRef.current);
         saveTimeoutRef.current = null;
       }
       
       const textarea = textareaRef.current;
       if (textarea) {
         // Store cursor position
         const selectionStart = textarea.selectionStart || 0;
         const selectionEnd = textarea.selectionEnd || 0;
         
         // Update content - this will cause onChange but that's OK
         setContent(data.content);
         setHasUnsavedChanges(false);
         
         // Restore cursor position after React updates
         setTimeout(() => {
           textarea.setSelectionRange(selectionStart, selectionEnd);
           textarea.focus();
         }, 10);
       }
     } else {
       console.log('🚫 Ignoring update from same session (avoiding echo)');
     }
  };

  // WebSocket connection - EXACTLY like chat
  useEffect(() => {
    console.log('🔌 Connecting to WebSocket for document collaboration');
    
    websocketService.connect();
    
    websocketService.on('connected', (connected: boolean) => {
      console.log('🔗 WebSocket connection status:', connected);
      setIsConnected(connected);
      
      if (connected) {
        console.log('📝 Joining document collaboration room');
        websocketService.joinDocument();
      }
    });

    websocketService.on('document-updated', handleDocumentUpdate);
    
    return () => {
      console.log('🔌 Cleaning up WebSocket connections');
      websocketService.off('connected', () => {});
      websocketService.off('document-updated', handleDocumentUpdate);
      websocketService.leaveDocument();
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [sessionId]);

  // Load document on mount
  useEffect(() => {
    loadDocument();
  }, []);

  // Auto-save on unmount
  useEffect(() => {
    return () => {
      if (hasUnsavedChanges && content) {
        // Force save on unmount if there are unsaved changes
        saveDocument(content);
      }
    };
  }, [hasUnsavedChanges, content]);

  if (isLoading) {
    return (
      <div className="notepad">
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-500">Loading document...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="notepad">
      <div className="h-full flex flex-col">
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
          <span className="text-md font-medium text-gray-700">
            Whatever you write here, other people will also see, share love &lt;3
          </span>
        </div>
        <textarea 
          ref={textareaRef}
          value={content}
          onChange={handleTextChange}
          className={`notepad-textarea ${className || ''}`}
          placeholder="Start typing your message..."
          style={{ 
            flex: 1,
            resize: 'none',
            outline: 'none',
            border: 'none',
            padding: '10px',
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: '1.4'
          }}
        />
      </div>
    </div>
  );
};

export default CollaborativeNotepad; 